/** "Change type" (deliverable 17): choose a target, read the preview in words, then commit it. */
import { ApiError, type DocTypeV1, type DocumentV1, type RetypeReportV1 } from '@codex/contracts';
import { Button, Dialog, TypeGlyph } from '@codex/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState } from 'react';
import { typeHref } from '../href.js';
import { describeRefusal } from '../model/refusals.js';
import { describeRetype } from '../model/retype.js';
import { isLook } from '../model/types-view.js';
import { retypeTargets } from '../model/values.js';
import { RefusalBanner } from '../parts.js';
import { docQuarantineKey } from './doc-fields.js';
import { usePillServices } from './services.js';

export const UPDATED_PREVIEW = 'This page changed. Here is the updated preview.';

interface Shown {
  readonly typeId: string;
  readonly body: { typeId: string; baseMetaVersion: number };
  readonly report: RetypeReportV1;
}

export function RetypeDialog({
  doc,
  types,
  onClose,
  refreshDoc,
  reload,
}: {
  doc: DocumentV1;
  types: readonly DocTypeV1[];
  onClose: () => void;
  refreshDoc: () => Promise<DocumentV1 | undefined>;
  reload: () => void;
}) {
  const services = usePillServices();
  const client = useQueryClient();
  const group = useId();
  const targets = retypeTargets(types, doc.typeId);
  const [chosen, setChosen] = useState<string | null>(null);
  const [shown, setShown] = useState<Shown | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const latest = useRef(0);
  const nameOf = (id: string) => types.find((type) => type.id === id)?.name ?? '';

  /** One preview; on a stale page, reads it again and previews once more. */
  const preview = async (typeId: string, version: number, retry: boolean): Promise<void> => {
    const ticket = ++latest.current;
    setBusy(true);
    setProblem(null);
    setBlocked(false);
    const body = { typeId, baseMetaVersion: version };
    try {
      const report = await services.retypePreview(doc.id, body);
      if (ticket !== latest.current) return;
      setShown({ typeId, body, report });
    } catch (error) {
      if (ticket !== latest.current) return;
      setShown(null);
      if (error instanceof ApiError && error.code === 'version_conflict' && retry) {
        const fresh = await refreshDoc();
        if (ticket !== latest.current) return;
        if (fresh) {
          setNote(UPDATED_PREVIEW);
          await preview(typeId, fresh.metaVersion, false);
          return;
        }
      }
      setBlocked(
        error instanceof ApiError &&
          (error.code === 'retype_blocked' || error.code === 'document_trashed'),
      );
      setProblem(describeRefusal(error));
    } finally {
      if (ticket === latest.current) setBusy(false);
    }
  };

  const choose = (typeId: string) => {
    setChosen(typeId);
    setShown(null);
    setNote(null);
    void preview(typeId, doc.metaVersion, true);
  };

  const commit = async () => {
    if (!shown || shown.typeId !== chosen) return;
    const ticket = ++latest.current;
    setBusy(true);
    setProblem(null);
    try {
      await services.retype(doc.id, shown.body);
      if (ticket !== latest.current) return;
      setDone(nameOf(shown.typeId));
      setShown(null);
      reload();
      void client.invalidateQueries({ queryKey: docQuarantineKey(doc.id) });
    } catch (error) {
      if (ticket !== latest.current) return;
      if (error instanceof ApiError && error.code === 'version_conflict') {
        const fresh = await refreshDoc();
        if (ticket !== latest.current) return;
        if (fresh && chosen) {
          setNote(UPDATED_PREVIEW);
          setBusy(false);
          await preview(chosen, fresh.metaVersion, false);
          return;
        }
      }
      setShown(null);
      setBlocked(
        error instanceof ApiError &&
          (error.code === 'retype_blocked' || error.code === 'document_trashed'),
      );
      setProblem(describeRefusal(error));
    } finally {
      if (ticket === latest.current) setBusy(false);
    }
  };

  const words = shown ? describeRetype(shown.report, types) : null;
  const canCommit = shown !== null && shown.typeId === chosen && !busy && !blocked && done === null;
  const current = types.find((type) => type.id === doc.typeId);
  return (
    <Dialog
      open
      onClose={onClose}
      busy={busy}
      title="Change type"
      testId="types.retype.report"
      description={`This page is ${current ? `a ${current.name}` : 'typed'} now. Nothing changes until you press Change type.`}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {done === null ? 'Cancel' : 'Close'}
          </Button>
          {done === null && (
            <Button
              variant="primary"
              disabled={!canCommit}
              busy={busy && shown !== null}
              onClick={() => void commit()}
            >
              Change type
            </Button>
          )}
        </>
      }
    >
      {done !== null ? (
        <p role="status">Changed to {done}.</p>
      ) : (
        <>
          <fieldset className="cx-pills__targets">
            <legend>Change to</legend>
            {targets.map((type) => (
              <label key={type.id} className="cx-pills__target">
                <input
                  type="radio"
                  name={group}
                  value={type.id}
                  checked={chosen === type.id}
                  disabled={busy}
                  onChange={() => choose(type.id)}
                />
                <TypeGlyph type={isLook(type.icon) ? type.icon : 'note'} size={16} />
                <span>{type.name}</span>
              </label>
            ))}
          </fieldset>
          <a className="cx-pills__link" href={typeHref(current?.key ?? '')}>
            Edit this type
          </a>
          {note !== null && <p role="status">{note}</p>}
          {problem !== null && <RefusalBanner>{problem}</RefusalBanner>}
          {words && (
            <section aria-label="What will happen" className="cx-pills__report">
              <h3>What will happen</h3>
              {words.fields.length > 0 ? (
                <ul>
                  {words.fields.map((line) => (
                    <li key={line.key}>{line.text}</li>
                  ))}
                </ul>
              ) : (
                <p>No field values change.</p>
              )}
              {words.summary.length > 0 && (
                <ul>
                  {words.summary.map((line) => (
                    <li key={line.key}>{line.text}</li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}
    </Dialog>
  );
}
